import React, { useState, useEffect } from 'react';
import { useTexts } from '../../hooks/useTexts';
import { useReferenceData } from '../../hooks/useReferenceData';
import { User, StatutMarital, RegimeMatrimonial } from '../../types/api';

interface ExtendedProfileFormProps {
  user: User;
  onSubmit: (data: Partial<User>) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

export const ExtendedProfileForm: React.FC<ExtendedProfileFormProps> = ({
  user,
  onSubmit,
  onCancel,
  isLoading = false
}) => {
  const { getText } = useTexts();
  const { statutsMaritaux, regimesMatrimoniaux, loading: refLoading } = useReferenceData();
  
  const [formData, setFormData] = useState<Partial<User>>({
    nom: user.nom || '',
    prenoms: user.prenoms || '',
    telephone: user.telephone || '',
    adresse: user.adresse || '',
    lieu_naissance: user.lieu_naissance || '',
    nationalite: user.nationalite || '',
    numero_piece_identite: user.numero_piece_identite || '',
    entreprise: user.entreprise || '',
    profession: user.profession || '',
    revenu_mensuel: user.revenu_mensuel || 0,
    domiciliation_bancaire: user.domiciliation_bancaire || '',
    statut_marital: user.statut_marital || undefined,
    regime_matrimonial: user.regime_matrimonial || undefined,
    nombre_enfants: user.nombre_enfants || 0,
    date_naissance: user.date_naissance || ''
  });
  
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    setFormData({
      nom: user.nom || '',
      prenoms: user.prenoms || '',
      telephone: user.telephone || '',
      adresse: user.adresse || '',
      lieu_naissance: user.lieu_naissance || '',
      nationalite: user.nationalite || '',
      numero_piece_identite: user.numero_piece_identite || '',
      entreprise: user.entreprise || '',
      profession: user.profession || '',
      revenu_mensuel: user.revenu_mensuel || 0,
      domiciliation_bancaire: user.domiciliation_bancaire || '',
      statut_marital: user.statut_marital || undefined,
      regime_matrimonial: user.regime_matrimonial || undefined,
      nombre_enfants: user.nombre_enfants || 0,
      date_naissance: user.date_naissance || ''
    });
  }, [user]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    
    let processedValue: any = value;
    
    if (type === 'number') {
      processedValue = value ? parseFloat(value) : 0;
    } else if (name === 'statut_marital' || name === 'regime_matrimonial') {
      processedValue = value ? parseInt(value) : undefined;
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: processedValue
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.nom?.trim()) {
      newErrors.nom = 'Le nom est requis';
    }

    if (!formData.prenoms?.trim()) {
      newErrors.prenoms = 'Les prénoms sont requis';
    }

    if (!formData.telephone?.trim()) {
      newErrors.telephone = 'Le téléphone est requis';
    }

    if (!formData.adresse?.trim()) {
      newErrors.adresse = 'L\'adresse est requise';
    }

    if (formData.revenu_mensuel && formData.revenu_mensuel < 0) {
      newErrors.revenu_mensuel = 'Le revenu mensuel ne peut pas être négatif';
    }

    if (formData.nombre_enfants && formData.nombre_enfants < 0) {
      newErrors.nombre_enfants = 'Le nombre d\'enfants ne peut pas être négatif';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  if (refLoading) {
    return (
      <div className="flex justify-center items-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-8">
      {/* Informations de base */}
      <div className="bg-gray-50 p-6 rounded-lg">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          {getText('profile.sections.basic_info')}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="nom" className="block text-sm font-medium text-gray-700 mb-2">
              Nom *
            </label>
            <input
              type="text"
              id="nom"
              name="nom"
              value={formData.nom || ''}
              onChange={handleInputChange}
              className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                errors.nom ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder={getText('placeholder.name')}
            />
            {errors.nom && (
              <p className="mt-1 text-sm text-red-600">{errors.nom}</p>
            )}
          </div>

          <div>
            <label htmlFor="prenoms" className="block text-sm font-medium text-gray-700 mb-2">
              Prénoms *
            </label>
            <input
              type="text"
              id="prenoms"
              name="prenoms"
              value={formData.prenoms || ''}
              onChange={handleInputChange}
              className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                errors.prenoms ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder={getText('placeholder.firstname')}
            />
            {errors.prenoms && (
              <p className="mt-1 text-sm text-red-600">{errors.prenoms}</p>
            )}
          </div>

          <div>
            <label htmlFor="telephone" className="block text-sm font-medium text-gray-700 mb-2">
              Téléphone *
            </label>
            <input
              type="tel"
              id="telephone"
              name="telephone"
              value={formData.telephone || ''}
              onChange={handleInputChange}
              className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                errors.telephone ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder={getText('placeholder.phone')}
            />
            {errors.telephone && (
              <p className="mt-1 text-sm text-red-600">{errors.telephone}</p>
            )}
          </div>

          <div>
            <label htmlFor="adresse" className="block text-sm font-medium text-gray-700 mb-2">
              Adresse *
            </label>
            <textarea
              id="adresse"
              name="adresse"
              value={formData.adresse || ''}
              onChange={handleInputChange}
              rows={3}
              className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                errors.adresse ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Votre adresse complète"
            />
            {errors.adresse && (
              <p className="mt-1 text-sm text-red-600">{errors.adresse}</p>
            )}
          </div>
        </div>
      </div>

      {/* Informations personnelles */}
      <div className="bg-gray-50 p-6 rounded-lg">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          {getText('profile.sections.personal_info')}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="date_naissance" className="block text-sm font-medium text-gray-700 mb-2">
              {getText('profile.form.birth_date')}
            </label>
            <input
              type="date"
              id="date_naissance"
              name="date_naissance"
              value={formData.date_naissance || ''}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label htmlFor="lieu_naissance" className="block text-sm font-medium text-gray-700 mb-2">
              {getText('profile.form.birth_place')}
            </label>
            <input
              type="text"
              id="lieu_naissance"
              name="lieu_naissance"
              value={formData.lieu_naissance || ''}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Ville de naissance"
            />
          </div>

          <div>
            <label htmlFor="nationalite" className="block text-sm font-medium text-gray-700 mb-2">
              {getText('profile.form.nationality')}
            </label>
            <input
              type="text"
              id="nationalite"
              name="nationalite"
              value={formData.nationalite || ''}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Votre nationalité"
            />
          </div>

          <div>
            <label htmlFor="numero_piece_identite" className="block text-sm font-medium text-gray-700 mb-2">
              {getText('profile.form.id_number')}
            </label>
            <input
              type="text"
              id="numero_piece_identite"
              name="numero_piece_identite"
              value={formData.numero_piece_identite || ''}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="N° CNI/Passeport"
            />
          </div>
        </div>
      </div>

      {/* Informations professionnelles */}
      <div className="bg-gray-50 p-6 rounded-lg">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          {getText('profile.sections.professional_info')}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="entreprise" className="block text-sm font-medium text-gray-700 mb-2">
              {getText('profile.form.company')}
            </label>
            <input
              type="text"
              id="entreprise"
              name="entreprise"
              value={formData.entreprise || ''}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Nom de l'entreprise"
            />
          </div>

          <div>
            <label htmlFor="profession" className="block text-sm font-medium text-gray-700 mb-2">
              {getText('profile.form.profession')}
            </label>
            <input
              type="text"
              id="profession"
              name="profession"
              value={formData.profession || ''}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Votre profession"
            />
          </div>

          <div>
            <label htmlFor="revenu_mensuel" className="block text-sm font-medium text-gray-700 mb-2">
              {getText('profile.form.monthly_income')}
            </label>
            <input
              type="number"
              id="revenu_mensuel"
              name="revenu_mensuel"
              value={formData.revenu_mensuel || ''}
              onChange={handleInputChange}
              min="0"
              step="1000"
              className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                errors.revenu_mensuel ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="0"
            />
            {errors.revenu_mensuel && (
              <p className="mt-1 text-sm text-red-600">{errors.revenu_mensuel}</p>
            )}
          </div>

          <div>
            <label htmlFor="domiciliation_bancaire" className="block text-sm font-medium text-gray-700 mb-2">
              {getText('profile.form.bank_domiciliation')}
            </label>
            <input
              type="text"
              id="domiciliation_bancaire"
              name="domiciliation_bancaire"
              value={formData.domiciliation_bancaire || ''}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Nom de la banque"
            />
          </div>
        </div>
      </div>

      {/* Situation familiale */}
      <div className="bg-gray-50 p-6 rounded-lg">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          {getText('profile.sections.family_info')}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="statut_marital" className="block text-sm font-medium text-gray-700 mb-2">
              {getText('profile.form.marital_status')}
            </label>
            <select
              id="statut_marital"
              name="statut_marital"
              value={formData.statut_marital || ''}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">{getText('profile.form.select_status')}</option>
              {statutsMaritaux.map((statut) => (
                <option key={statut.id} value={statut.id}>
                  {statut.libelle}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="regime_matrimonial" className="block text-sm font-medium text-gray-700 mb-2">
              {getText('profile.form.marital_regime')}
            </label>
            <select
              id="regime_matrimonial"
              name="regime_matrimonial"
              value={formData.regime_matrimonial || ''}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">{getText('profile.form.select_regime')}</option>
              {regimesMatrimoniaux.map((regime) => (
                <option key={regime.id} value={regime.id}>
                  {regime.libelle}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="nombre_enfants" className="block text-sm font-medium text-gray-700 mb-2">
              {getText('profile.form.children_count')}
            </label>
            <input
              type="number"
              id="nombre_enfants"
              name="nombre_enfants"
              value={formData.nombre_enfants || ''}
              onChange={handleInputChange}
              min="0"
              className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                errors.nombre_enfants ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="0"
            />
            {errors.nombre_enfants && (
              <p className="mt-1 text-sm text-red-600">{errors.nombre_enfants}</p>
            )}
          </div>
        </div>
      </div>

      <div className="flex justify-end space-x-3">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          {getText('contact_persons.form.cancel')}
        </button>
        <button
          type="submit"
          disabled={isLoading}
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? (
            <div className="flex items-center">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              {getText('contact_persons.form.update')}
            </div>
          ) : (
            getText('contact_persons.form.update')
          )}
        </button>
      </div>
    </form>
  );
};
